import _, { isArray } from 'lodash';
import moment from 'moment';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityAttribute, useEntityAttributes } from '~/data/attributes';
import { Enum, useAllEnums } from '~/data/enums';
import { ProposalProps, ProposalStatus } from '~/data/proposals';
import { EmploymentUpcomingChangeDifference } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Common/EmploymentUpcomingChanges/component';

type FieldValue = number | boolean | string | undefined;
type CustomFieldValue = FieldValue | string[];

type CustomFieldValueEntries = {
  [p: string]: CustomFieldValue;
};

export type UpcomingChangeValueFormatter = (
  valueKey: string,
  value: FieldValue,
) => string;

type StandardFieldsResolverProps<TFieldsType> = {
  fieldKeys: string[];
  fieldKeyExceptions?: string[];
  oldData: TFieldsType;
  newData: TFieldsType;
  labelTranslator?: (key: keyof TFieldsType) => string;
  valueFormatter?: UpcomingChangeValueFormatter;
};

type CustomFieldsResolverProps = {
  customEnumGroupKey: string;
  customDataEntity: string;
  oldData: CustomFieldValueEntries;
  newData: CustomFieldValueEntries;
};

type CustomDataEnum = Enum & {
  type?: string;
  subItems?: Enum[];
};

type UpcomingChangeEvent = {
  label: string;
  valueChanges: EmploymentUpcomingChangeDifference[];
};

type UpcomingProposalChangeEvent = UpcomingChangeEvent & {
  proposalId: string;
  proposalStatus: ProposalStatus;
};

export const useResolveUpcomingChanges = () => {
  const { t } = useTranslation();
  const { allEnums, isLoading: isLoadingAllEnums } = useAllEnums();
  const { allAttributes, isLoading: isLoadingEntityAttributes } =
    useEntityAttributes();

  const getCustomDataAttributes = useCallback(
    (customDataEntity: string): EntityAttribute[] => {
      return Object.values(allAttributes)
        .flat()
        .filter((a) => a.fCustomDataEntity === customDataEntity);
    },
    [allAttributes],
  );

  const getCustomEnums = useCallback(
    (enumGroupKey: string): Enum[] => {
      return !!allEnums[enumGroupKey] ? allEnums[enumGroupKey] : [];
    },
    [allEnums],
  );

  const resolveStandardFieldsValueChanges = useCallback(
    <TFieldsType>(props: StandardFieldsResolverProps<TFieldsType>) => {
      const {
        fieldKeys,
        fieldKeyExceptions,
        oldData,
        newData,
        labelTranslator,
        valueFormatter,
      } = props;

      const formatValue = (
        key: string,
        value: TFieldsType[keyof TFieldsType],
      ): FieldValue => {
        const castValue = value as unknown as FieldValue;

        return !!valueFormatter ? valueFormatter(key, castValue) : castValue;
      };

      return fieldKeys.reduce((acc, key) => {
        const fieldKey = key as keyof TFieldsType;
        const oldValue = oldData[fieldKey];
        const newValue = newData[fieldKey];

        if (
          oldValue === newValue ||
          (!!fieldKeyExceptions && fieldKeyExceptions.includes(key))
        ) {
          return acc;
        }

        // Objects & Arrays should be handled separately
        if (
          isArray(oldValue) ||
          isArray(newValue) ||
          typeof oldValue === 'object' ||
          typeof newValue === 'object'
        ) {
          console.groupCollapsed(
            `WARNING: resolveStandardFieldsValueChanges-function cannot determine specific changes in Objects or Arrays.`,
          );
          console.log(
            `Please exclude the property "${String(
              fieldKey,
            )}" from processed properties.`,
          );
          if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            console.log(
              `The property "${String(
                fieldKey,
              )}" appears to contain differences between versions.`,
            );
          }
          console.groupEnd();

          return acc;
        }

        const newEntry: EmploymentUpcomingChangeDifference = {
          key: key,
          label: !!labelTranslator ? labelTranslator(fieldKey) : t(key),
          oldValue: formatValue(key, oldValue),
          newValue: formatValue(key, newValue),
        };
        const result: EmploymentUpcomingChangeDifference[] = [...acc, newEntry];

        return result;
      }, [] as EmploymentUpcomingChangeDifference[]);
    },
    [t],
  );

  const resolveCustomDataFieldsKeysAndChanges = useCallback(
    (customDataEntries: CustomFieldValueEntries) => {
      const customDataKeys = Object.keys(customDataEntries);

      //Add custom data fields to old array, custom data is an object, it should be split as key value pairs
      if (!_.isEmpty(customDataEntries)) {
        const result = Object.assign(
          {},
          ...Object.entries(customDataEntries).map(([key, value]) => ({
            [key]: value as FieldValue,
          })),
        );

        return {
          keys: customDataKeys,
          dataArray: result,
        };
      }

      return {
        keys: customDataKeys,
        dataArray: [],
      };
    },
    [],
  );

  const resolveCustomDataEnumsAndOptions = useCallback(
    (enumGroupKey, entityKey): CustomDataEnum[] => {
      const enums = getCustomEnums(enumGroupKey);
      const attrs = getCustomDataAttributes(entityKey);

      return enums.map((e) => {
        const attr = attrs.find((a) => a.fCustomDataAttributeName === e.code);

        if (attr) {
          const subGroupKey = attr?.fCustomDataAttributeDataSubtype;
          const subItems = !!subGroupKey
            ? getCustomEnums(subGroupKey)
            : undefined;

          return {
            ...e,
            subItems,
            type: attr.fCustomDataAttributeDataType,
          };
        }

        return { ...e };
      });
    },
    [getCustomDataAttributes, getCustomEnums],
  );

  const resolveCustomFieldsValueChanges = useCallback(
    (
      props: CustomFieldsResolverProps,
    ): EmploymentUpcomingChangeDifference[] => {
      const { oldData, newData, customEnumGroupKey, customDataEntity } = props;

      const customDataEnums = resolveCustomDataEnumsAndOptions(
        customEnumGroupKey,
        customDataEntity,
      );

      const getCustomEnum = (key: string): Enum | undefined => {
        return customDataEnums.find((e) => e.code === key);
      };

      const customDataValueFormatter = (
        customDataEnum: CustomDataEnum,
        value?: string | string[] | number | boolean | undefined,
      ): string => {
        if (typeof value === 'undefined') {
          return '';
        }

        switch (customDataEnum?.type) {
          case 'CHECKBOX':
            return !!value ? t('laYes') : t('laNo');
          case 'MULTISELECT':
            if (isArray(value)) {
              return (
                customDataEnum?.subItems
                  ?.filter((e) => value.includes(e.code))
                  .map((e) => e.name)
                  .join(', ') ?? ''
              );
            }
            return '';
          case 'ENUM':
            return (
              customDataEnum?.subItems?.find((e) => value === e.code)?.name ??
              `${value}`
            );
          case 'DATE':
          case 'TEXT':
          default:
            return `${value}`;
        }
      };

      const oldCustomData = resolveCustomDataFieldsKeysAndChanges(
        oldData ?? {},
      );
      const newCustomData = resolveCustomDataFieldsKeysAndChanges(
        newData ?? {},
      );

      const allCustomFieldKeys = _.uniq([
        ...oldCustomData.keys,
        ...newCustomData.keys,
      ]);

      const isCustomValueMatch = (
        oldValue: any, // eslint-disable-line @typescript-eslint/no-explicit-any
        newValue: any, // eslint-disable-line @typescript-eslint/no-explicit-any
        type?: string,
      ) => {
        switch (type) {
          case 'DATE':
            return moment(oldValue).isSame(newValue, 'day');
          case 'MULTISELECT':
            return JSON.stringify(oldValue) === JSON.stringify(newValue);
          default:
            return oldValue === newValue;
        }
      };

      if (allCustomFieldKeys.length) {
        return allCustomFieldKeys.reduce((acc, key: string) => {
          const customFieldEnum = getCustomEnum(key);
          const oldValue = oldCustomData.dataArray[key];
          const newValue = newCustomData.dataArray[key];

          if (isCustomValueMatch(oldValue, newValue, customFieldEnum?.type)) {
            return acc;
          }

          const formatValue = customDataValueFormatter;

          const newEntry: EmploymentUpcomingChangeDifference = {
            key: key,
            label: customFieldEnum?.name,
            oldValue: !!customFieldEnum
              ? formatValue(customFieldEnum, oldValue)
              : oldValue,
            newValue: !!customFieldEnum
              ? formatValue(customFieldEnum, newValue)
              : newValue,
          };

          return [...acc, newEntry];
        }, [] as EmploymentUpcomingChangeDifference[]);
      }

      return [];
    },
    [
      resolveCustomDataFieldsKeysAndChanges,
      resolveCustomDataEnumsAndOptions,
      t,
    ],
  );

  const resolveUpcomingChanges = useCallback(
    <TFieldsType>(props: {
      standardFields: StandardFieldsResolverProps<TFieldsType>;
      customFields?: CustomFieldsResolverProps;
    }): EmploymentUpcomingChangeDifference[] => {
      const { standardFields, customFields } = props;

      const standardChanges = resolveStandardFieldsValueChanges(standardFields);
      const customChanges = !!customFields
        ? resolveCustomFieldsValueChanges(customFields)
        : [];

      return [...standardChanges, ...customChanges];
    },
    [resolveStandardFieldsValueChanges, resolveCustomFieldsValueChanges],
  );

  const resolveProposalsChangesSequence = useCallback(
    <TProposalProps extends ProposalProps, TLegacyProps>(props: {
      proposals: TProposalProps[];
      proposalToLegacyProps: (proposal: TProposalProps) => TLegacyProps;
      defaultData: TLegacyProps;
      labelResolver: (proposal: TProposalProps) => string;
      valueChangesResolver: (
        previous: TLegacyProps,
        next: TLegacyProps,
      ) => EmploymentUpcomingChangeDifference[];
    }): {
      upcoming: UpcomingProposalChangeEvent[];
      onGoing: UpcomingProposalChangeEvent | undefined;
    } => {
      const {
        proposals,
        proposalToLegacyProps,
        defaultData,
        labelResolver,
        valueChangesResolver,
      } = props;
      const getPreviousScheduled = (list: TProposalProps[], index: number) => {
        const lastIndex = list.length - 1;
        if (index >= lastIndex) {
          return defaultData;
        }

        const prevProposal = proposals[index + 1];
        return !prevProposal
          ? defaultData
          : proposalToLegacyProps(prevProposal);
      };

      const proposalChanges: UpcomingProposalChangeEvent[] = proposals
        .filter((p) =>
          [ProposalStatus.COMPLETED, ProposalStatus.IN_PROGRESS].includes(
            p.proposalStatus,
          ),
        )
        .map((proposal, index, list) => {
          const { proposalId, proposalStatus } = proposal;

          return {
            proposalId,
            proposalStatus,
            label: labelResolver(proposal),
            valueChanges: valueChangesResolver(
              getPreviousScheduled(list, index),
              proposalToLegacyProps(proposal),
            ),
          };
        });

      const filterByStatus = (
        status: ProposalStatus,
      ): UpcomingProposalChangeEvent[] =>
        proposalChanges.filter((c) => c.proposalStatus === status);

      return {
        upcoming: filterByStatus(ProposalStatus.COMPLETED),
        onGoing: filterByStatus(ProposalStatus.IN_PROGRESS)[0],
      };
    },
    [],
  );

  return {
    resolveStandardFieldsValueChanges,
    resolveUpcomingChanges,
    isLoading: isLoadingAllEnums || isLoadingEntityAttributes,
    resolveProposalsChangesSequence,
  };
};
